import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Container,
  Card,
  CardContent,
  Link,
  Divider,
  useTheme,
  Fade,
  CircularProgress,
  Alert,
} from "@mui/material";
import { 
  Login as LoginIcon, 
  Visibility, 
  VisibilityOff,
  Email,
} from "@mui/icons-material";
import BrandIcon from "../../components/common/BrandIcon";
import { IconButton, InputAdornment } from "@mui/material";
import { HOME_ROUTE_PATH, REGISTER_ROUTE_PATH, VERIFY_EMAIL_ROUTE_PATH, FORGOT_PASSWORD_ROUTE_PATH } from "../../utils/constants/AppConstants";
import { useError } from "../../error/useError";
import ErrorAlert from "../../components/common/ErrorAlert";
import { useAuth } from "../../auth/AuthContext";

type LocationState = { message?: string } | null;

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const state = (location.state as LocationState) || null;
  const [infoMessage, setInfoMessage] = useState<string | null>(state?.message || null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const REMEMBER_ME_PREF_KEY = 'remember-me';
  const [extendedSession, setExtendedSession] = useState(() => {
    try {
      const pref = localStorage.getItem(REMEMBER_ME_PREF_KEY);
      return pref ? pref === 'true' : false; // default unchecked
    } catch (e) {
      return false;
    }
  });
  const { error, helpText, isLoading, clearError, handleError, setLoading } = useError();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      setLoading(true);
      clearError();
      const result = await login(email, password, extendedSession);
      if (result.emailVerified) {
        navigate(HOME_ROUTE_PATH);
      } else {
        navigate(VERIFY_EMAIL_ROUTE_PATH, { state: { email: result.email } });
      }
    } catch (e: any) {
      handleError(e, "Login failed. Please check your credentials and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((show) => !show);
  };

  // Clear the navigation state message after showing it
  useEffect(() => {
    if (state?.message) {
      // Clear the state from history to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [state]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        display: "flex",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={800}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 5 }}>
              {/* Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <BrandIcon size={64} />
                </Box>
                
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Welcome Back
                </Typography>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  Sign in to your PocketBalance account
                </Typography>
              </Box>

              {/* Info Message (e.g., from email change) */}
              {infoMessage && (
                <Alert 
                  severity="success" 
                  onClose={() => setInfoMessage(null)}
                  sx={{ mb: 2 }}
                >
                  {infoMessage}
                </Alert>
              )}

              {/* Error Alert */}
              <ErrorAlert
                error={error}
                helpText={helpText}
                onClear={clearError}
                title="Login Failed"
                showRetry={false}
              />

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  sx={{ mb: 4 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 1, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        id="remember-me"
                        checked={extendedSession}
                        onChange={(e) => {
                          setExtendedSession(e.target.checked);
                          try {
                            localStorage.setItem(REMEMBER_ME_PREF_KEY, String(e.target.checked));
                          } catch (err) {
                            // ignore storage errors
                          }
                        }}
                        disabled={isLoading}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Keep me signed in"
                  />

                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate(FORGOT_PASSWORD_ROUTE_PATH)}
                    sx={{ color: theme.palette.primary.main, whiteSpace: 'nowrap' }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                  sx={{
                    py: 1.5,
                    mb: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 8px 25px ${theme.palette.primary.main}30`,
                    },
                    '&:disabled': {
                      background: theme.palette.action.disabledBackground,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Don't have an account?
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: "center" }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body1"
                    onClick={() => navigate(REGISTER_ROUTE_PATH)}
                    sx={{
                      textDecoration: 'none',
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      '&:hover': {
                        color: theme.palette.primary.dark,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Create your free account →
                  </Link>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default LoginPage;
